import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TopBanner from "../../../components/topBanner/topBanner";
import TitleCenter from "../../../components/title/TitleCenter";
import { getConsultingsByNavMenuId } from "../../../apis/consultings";
import { getAdminConsultings } from "../../../apis/adminConsultings";
import { getAllAdminNavMenus } from "../../../apis/adminNavMenus";
import { getNavMenuByLanguage } from "../../../apis/navMenus";
import { normalizeLang } from "../../../utils/localeDataLoader";
import bannerImg from "../../../images/banner/banner-tin-tuc.jpg";
import { FiArrowUpRight } from "react-icons/fi";

const PARENT_KEY = "menu.investment";

const getResponseItems = (response) => {
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
};

const getMenuTitle = (menu) =>
  menu?.title ||
  menu?.Title ||
  menu?.name ||
  menu?.Name ||
  menu?.translations?.[0]?.title ||
  menu?.translations?.[0]?.Title ||
  "";

const getInvestmentMenus = (menus = []) => {
  const parent = menus.find(
    (menu) => menu.keyName === PARENT_KEY || menu.KeyName === PARENT_KEY,
  );

  if (!parent) return [];
  if (Array.isArray(parent.children) && parent.children.length > 0) {
    return [...parent.children].sort(
      (a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0),
    );
  }

  return menus
    .filter(
      (menu) => Number(menu.parentId || menu.ParentId) === Number(parent.id || parent.Id),
    )
    .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
};

const buildConsultingGroups = (consultings = [], menus = []) => {
  const investmentMenus = getInvestmentMenus(menus);
  const menuList =
    investmentMenus.length > 0
      ? investmentMenus
      : [
          ...new Map(
            consultings.map((item) => [
              item.navMenuId,
              { id: item.navMenuId, title: `Menu ${item.navMenuId}` },
            ]),
          ).values(),
        ];

  return menuList
    .map((menu) => {
      const navMenuId = Number(menu.id || menu.Id);
      const items = consultings
        .filter((item) => Number(item.navMenuId) === navMenuId)
        .filter((item) => item.isActive !== false)
        .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));

      return {
        navMenuId,
        title: getMenuTitle(menu),
        items,
      };
    })
    .filter((group) => group.items.length > 0);
};

function ConsultingServices() {
  const [groups, setGroups] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const expectedPath = lang === "vi" ? "/dau-tu-dich-vu" : "/investment-services";
    const normalizedPath =
      location.pathname.endsWith("/") && location.pathname.length > 1
        ? location.pathname.slice(0, -1)
        : location.pathname;

    if (normalizedPath !== expectedPath) {
      navigate(expectedPath, { replace: true });
    }
  }, [lang, location.pathname, navigate]);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const [consultingResponse, navMenuResponse] = await Promise.all([
          getAdminConsultings({
            pageIndex: 1,
            pageSize: 1000,
            languageCode: lang,
            isActive: true,
          }),
          getAllAdminNavMenus({
            pageIndex: 1,
            pageSize: 1000,
            languageCode: lang,
          }),
        ]);
        const consultings = getResponseItems(consultingResponse);
        const menus = getResponseItems(navMenuResponse);
        const nextGroups = buildConsultingGroups(consultings, menus);

        if (isMounted) setGroups(nextGroups);
        return;
      } catch (error) {
        console.error("Loi khi lay danh sach consulting getAll:", error);
      }

      try {
        const menus = await getNavMenuByLanguage(lang);
        const investmentMenus = getInvestmentMenus(menus);
        const nextGroups = await Promise.all(
          investmentMenus.map(async (menu) => ({
            navMenuId: menu.id,
            title: getMenuTitle(menu),
            items: await getConsultingsByNavMenuId(lang, menu.id),
          })),
        );

        if (isMounted) {
          setGroups(nextGroups.filter((group) => group.items.length > 0));
        }
      } catch (error) {
        console.error("Loi khi lay danh sach consulting:", error);
        if (isMounted) setGroups([]);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [lang]);

  return (
    <div className="DetailPSCategory consulting-page">
      <TopBanner
        titleCenter={t("menu.investment")}
        imgBanner={bannerImg}
        pageName={t("menu.investment")}
        text={t("menu.investment")}
      />
      <div className="layout_container consulting-page__content">
        {groups.map((group) => (
          <section className="consulting-group" key={group.navMenuId}>
            <div className="consulting-group__heading">
              <TitleCenter text={group.title} />
            </div>
            <div className="PScategory">
              <ul className="consulting-grid">
                {group.items.map((item, index) => (
                  <li
                    key={item.id ?? index}
                    className="consulting-card"
                    style={{ "--ps-delay": `${index * 90}ms` }}
                  >
                    <div className="consulting-card__media">
                      <img src={item.image} alt={item.description} />
                    </div>
                    <div className="consulting-card__body">
                      <h4>{item.description}</h4>
                      <span className="consulting-card__arrow" aria-hidden="true"><FiArrowUpRight /></span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default ConsultingServices;
